import { isTouchDevice } from "../../common/device";
import { expressEventListener, expressQuerySelectorAll, htmlToElement } from "../../common/html";
import { compositeDisposable } from "../../utils/disposable";
import { getHtmlRequest } from "../../utils/fetch";
import { INavigationDesktopDeps } from "./navigation-desktop-first-level";

export function CreateNavigationDesktopSecondLevel(container: HTMLElement, deps: INavigationDesktopDeps) {
	const ACTIVE_CLASS = "m-navigation-desktop-second-level__item--active";
	const HIDE_CLASS = "u-hide";
	const LOADING_CLASS = "skeleton-loading";
	const loading = {}; // save the firstlevelindex with there loading sate
	const { activatedElement, getActiveIndex } = deps;
	let menuItems = expressQuerySelectorAll<HTMLElement>(container, ".technical-navigation-item");

	const addEventListenersToMenuItem = (x: HTMLElement) => {
		return compositeDisposable([
			expressEventListener(x, "click", () => {
				activatedElement(x);
				menuItems.forEach((ni) => ni.classList.toggle(ACTIVE_CLASS, ni === x));
			}),
			expressEventListener(x, "mouseover", () => {
				if (isTouchDevice()) return;
				activatedElement(x);
				menuItems.forEach((ni) => ni.classList.toggle(ACTIVE_CLASS, ni === x));
			}),
		]);
	};

	const disposible = menuItems.map((x) => addEventListenersToMenuItem(x));
	const open = async (firstLevelIndex: string): Promise<boolean> => {
		const secondMenuAlreadyLoaded = menuItems.some((x) => x.dataset.parentIndex === firstLevelIndex);
		if (!secondMenuAlreadyLoaded && !loading[firstLevelIndex]) {
			loading[firstLevelIndex] = true;
			container.classList.toggle(HIDE_CLASS, false);
			container.classList.toggle(LOADING_CLASS, true);
			const request = await getHtmlRequest(`apicore/headerapi/desktopmenu?indexLevel1=${firstLevelIndex}`);

			const requestNode = htmlToElement(request) as HTMLElement;
			const secondLevelItems = expressQuerySelectorAll<HTMLElement>(
				requestNode,
				`.technical-navigation-item[data-parent-index='${firstLevelIndex}'][data-has-third-level]`,
			);

			// add items to DOM && javascript variable
			const isStillActiveIndex = getActiveIndex() === firstLevelIndex;
			secondLevelItems.forEach((x) => {
				disposible.push(addEventListenersToMenuItem(x));
				x.classList.toggle(HIDE_CLASS, !isStillActiveIndex);
				container.appendChild(x);
			});
			menuItems = [...menuItems, ...secondLevelItems];
			loading[firstLevelIndex] = false;

			if (!isStillActiveIndex) return false; // don't update item not active anymore
		}

		menuItems.forEach((x) => {
			const active = x.dataset.parentIndex === firstLevelIndex;
			x.classList.toggle(HIDE_CLASS, !active);
			x.classList.toggle(ACTIVE_CLASS, false); // on open => no second level item selected yet (select on hover or click item)
		});
		container.classList.toggle(LOADING_CLASS, !!loading[firstLevelIndex]);
		container.classList.toggle(HIDE_CLASS, false);

		return true;
	};

	const close = () => {
		container.classList.toggle(HIDE_CLASS, true);
		menuItems.forEach((ni) => ni.classList.toggle(ACTIVE_CLASS, false));
	};

	return {
		dispose: () => disposible.forEach((x) => x.dispose()),
		open,
		close,
		resetSelected: () =>
			menuItems.forEach((ni) => ni.dataset.hasThirdLevel === "false" && ni.classList.toggle(ACTIVE_CLASS, false)),
	};
}
